import { Badge } from '@fsd/ui';
import { Building2, CheckCircle2 } from 'lucide-react';
import logoBlack from '@/assets/logo/black.svg';
import logoWhite from '@/assets/logo/white.svg';
import { useTranslation } from '@/i18n/hooks';
import { useTheme } from '@/store/useTheme';
import { useInView } from '@/utils';

export function About() {
  const { t } = useTranslation('home');
  const theme = useTheme((state) => state.theme);
  const logo = theme === 'dark' ? logoWhite : logoBlack;

  // Scroll-triggered animations
  const [sectionRef] = useInView({ threshold: 0.1 });
  const [headerRef, isHeaderInView] = useInView({ threshold: 0.2 });
  const [logoRef, isLogoInView] = useInView({ threshold: 0.2 });
  const [textRef, isTextInView] = useInView({ threshold: 0.2 });

  const values = [
    { key: 'about.values.quality' as const, icon: CheckCircle2 },
    { key: 'about.values.innovation' as const, icon: CheckCircle2 },
    { key: 'about.values.reliability' as const, icon: CheckCircle2 },
    { key: 'about.values.excellence' as const, icon: CheckCircle2 },
  ];

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      id="about"
      className="relative overflow-hidden bg-slate-50 py-16 sm:py-20 lg:py-32 dark:bg-slate-900"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 opacity-30 dark:opacity-20">
        <div className="absolute inset-0 bg-[linear-gradient(45deg,#1e293b08_1px,transparent_1px),linear-gradient(-45deg,#64748b08_1px,transparent_1px)] bg-size-[40px_40px]"></div>
      </div>
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-16 right-0 h-64 w-64 rounded-full bg-slate-200/20 blur-3xl animate-pulse sm:top-32 sm:right-32 dark:bg-slate-700/20"></div>
        <div className="absolute -bottom-16 left-0 h-80 w-80 rounded-full bg-slate-300/10 blur-3xl animate-pulse delay-1000 sm:bottom-32 sm:left-32 dark:bg-slate-600/10"></div>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="mb-12 text-center lg:mb-16"
        >
          <Badge
            variant="outline"
            size="lg"
            icon={<Building2 className="h-4 w-4" />}
            className={`mb-6 bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300 fade-in-ready ${isHeaderInView ? 'in-view' : ''}`}
          >
            {t('about.badge')}
          </Badge>

          <h2 className="mb-6 text-3xl font-bold text-slate-900 sm:text-4xl lg:text-6xl dark:text-slate-100">
            <span className={`block slide-in-left-ready ${isHeaderInView ? 'in-view' : ''}`}>
              {t('about.title.part1')}
            </span>
            <span
              className={`block text-primary slide-in-left-ready ${isHeaderInView ? 'in-view' : ''}`}
              style={{ transitionDelay: '200ms' }}
            >
              {t('about.title.part2')}
            </span>
          </h2>
        </div>

        {/* Main Content: Logo + Text */}
        <div className="mb-16 grid items-center gap-12 lg:mb-20 lg:grid-cols-2 lg:gap-16">
          {/* Logo - Left Side */}
          <div
            ref={logoRef as React.RefObject<HTMLDivElement>}
            className={`relative fade-in-right-ready ${isLogoInView ? 'in-view' : ''}`}
          >
            <div className="absolute -inset-2 rounded-2xl bg-slate-200 opacity-20 blur transition duration-1000 sm:-inset-4 dark:bg-slate-700 animate-pulse"></div>
            <div className="absolute -top-2 -right-2 h-12 w-12 rounded-full bg-slate-300/30 sm:-top-4 sm:-right-4 sm:h-16 sm:w-16 dark:bg-slate-600/30 animate-float"></div>
            <div className="absolute -bottom-2 -left-2 h-16 w-16 rounded-full bg-slate-400/20 sm:-bottom-4 sm:-left-4 sm:h-20 sm:w-20 dark:bg-slate-500/20 animate-float delay-1000"></div>

            <div className="relative rounded-2xl border border-slate-200 bg-white p-4 shadow-xl transition-all duration-500 hover:scale-105 hover:shadow-2xl sm:p-6 dark:border-slate-700 dark:bg-slate-800">
              <img
                src={logo}
                alt={t('about.title.part2')}
                className="w-full rounded-xl object-contain transition-transform duration-500 hover:scale-105"
              />
            </div>
          </div>

          {/* Text Content - Right Side */}
          <div
            ref={textRef as React.RefObject<HTMLDivElement>}
            className={`space-y-6 text-center lg:text-left fade-in-up-ready ${isTextInView ? 'in-view' : ''}`}
            style={{ transitionDelay: '300ms' }}
          >
            <div className="space-y-4 text-base leading-relaxed text-slate-600 sm:text-lg dark:text-slate-300">
              <p
                className={`fade-in-ready ${isTextInView ? 'in-view' : ''}`}
                style={{ transitionDelay: '400ms' }}
              >
                {t('about.description.paragraph1')}
              </p>
              <p
                className={`fade-in-ready ${isTextInView ? 'in-view' : ''}`}
                style={{ transitionDelay: '500ms' }}
              >
                {t('about.description.paragraph2')}
              </p>
              <p
                className={`fade-in-ready ${isTextInView ? 'in-view' : ''}`}
                style={{ transitionDelay: '600ms' }}
              >
                {t('about.description.paragraph3')}
              </p>
            </div>

            {/* Values */}
            <div
              className={`pt-6 fade-in-ready ${isTextInView ? 'in-view' : ''}`}
              style={{ transitionDelay: '700ms' }}
            >
              <h3 className="mb-4 text-xl font-semibold text-slate-900 dark:text-slate-100">
                {t('about.values.title')}
              </h3>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {values.map((value, index) => {
                  const IconComponent = value.icon;
                  return (
                    <div
                      key={value.key}
                      className={`group flex items-center gap-2 rounded-lg bg-white p-3 shadow-sm transition-all duration-300 hover:scale-105 hover:shadow-md dark:bg-slate-800 fade-in-ready ${isTextInView ? 'in-view' : ''}`}
                      style={{
                        transitionDelay: `${800 + index * 100}ms`,
                      }}
                    >
                      <IconComponent className="h-5 w-5 shrink-0 text-primary transition-transform duration-300 group-hover:scale-110" />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {t(value.key)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
